import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { ConvoSituationType, ConvoSession } from '../../types/convo.js';
import type { Person } from '../../types/person.js';
import { createSession, searchEmails, getEmail } from '../../services/convo-api.js';
import type { EmailSearchResult } from '../../services/convo-api.js';
import { getPeople } from '../../services/person-api.js';

const SITUATION_TYPES: { value: ConvoSituationType; label: string }[] = [
  { value: 'meeting', label: 'Meeting' },
  { value: 'negotiation', label: 'Verhandlung' },
  { value: 'sales', label: 'Sales' },
  { value: 'conflict', label: 'Konflikt' },
  { value: 'relationship', label: 'Beziehung' },
  { value: 'persuasion', label: 'Ueberzeugung' },
];

@customElement('cp-convo-session-form')
export class CpConvoSessionForm extends LitElement {
  createRenderRoot() { return this; }

  @state() private _open = false;
  @state() private _title = '';
  @state() private _description = '';
  @state() private _situationType: ConvoSituationType = 'meeting';
  @state() private _primaryGoal = '';
  @state() private _partnerId: number | undefined = undefined;
  @state() private _submitting = false;
  @state() private _error = '';
  @state() private _titleError = '';

  // People
  @state() private _people: Person[] = [];
  @state() private _peopleLoaded = false;

  // Email search
  @state() private _emailSearchOpen = false;
  @state() private _emailQuery = '';
  @state() private _emailResults: EmailSearchResult[] = [];
  @state() private _emailSearching = false;
  @state() private _emailError = '';
  @state() private _emailLoading = false;
  @state() private _emailSearched = false;
  @state() private _attachedEmailIds: Set<string> = new Set();

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  open(): void {
    this._resetForm();
    this._open = true;
    if (!this._peopleLoaded) {
      void this._loadPeople();
    }
  }

  private async _loadPeople(): Promise<void> {
    try {
      this._people = await getPeople();
      this._peopleLoaded = true;
    } catch {
      // Silently fail - dropdown will just be empty
    }
  }

  private _resetForm(): void {
    this._title = '';
    this._description = '';
    this._situationType = 'meeting';
    this._primaryGoal = '';
    this._partnerId = undefined;
    this._submitting = false;
    this._error = '';
    this._titleError = '';
    this._emailSearchOpen = false;
    this._emailQuery = '';
    this._emailResults = [];
    this._emailError = '';
    this._emailSearched = false;
    this._attachedEmailIds = new Set();
  }

  private _close(): void {
    this._open = false;
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this._open) {
      if (this._emailSearchOpen) {
        this._emailSearchOpen = false;
      } else {
        this._close();
      }
    }
  };

  private _onBackdropClick(e: Event): void {
    if ((e.target as HTMLElement).classList.contains('cp-convo-form__overlay')) {
      this._close();
    }
  }

  // Email search
  private async _searchEmails(): Promise<void> {
    const q = this._emailQuery.trim();
    if (!q) return;

    this._emailSearching = true;
    this._emailError = '';
    this._emailSearched = false;
    try {
      this._emailResults = await searchEmails(q);
      this._emailSearched = true;
    } catch (e) {
      this._emailError = e instanceof Error ? e.message : 'Email-Suche fehlgeschlagen';
    } finally {
      this._emailSearching = false;
    }
  }

  private async _attachEmail(emailId: string, account?: string): Promise<void> {
    if (this._attachedEmailIds.has(emailId)) return;
    this._emailLoading = true;
    try {
      const email = await getEmail(emailId, account);
      const emailRef = `--- Email-Thread ---\nVon: ${email.from}\nAn: ${email.to}\nDatum: ${email.date}\nBetreff: ${email.subject}\n\n${email.body}`;
      this._description = this._description
        ? `${this._description}\n\n${emailRef}`
        : emailRef;
      const updated = new Set(this._attachedEmailIds);
      updated.add(emailId);
      this._attachedEmailIds = updated;
    } catch (e) {
      this._emailError = e instanceof Error ? e.message : 'Email konnte nicht geladen werden';
    } finally {
      this._emailLoading = false;
    }
  }

  private async _onSubmit(): Promise<void> {
    const title = this._title.trim();
    if (!title) {
      this._titleError = 'Titel ist erforderlich';
      return;
    }

    this._submitting = true;
    this._error = '';

    try {
      const session = await createSession({
        title,
        description: this._description.trim() || undefined,
        situation_type: this._situationType,
        primary_goal: this._primaryGoal.trim() || undefined,
        partner_id: this._partnerId,
      });
      this._open = false;
      this.dispatchEvent(new CustomEvent<{ session: ConvoSession }>('session-created', {
        detail: { session },
        bubbles: true,
        composed: true,
      }));
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Session konnte nicht erstellt werden';
    } finally {
      this._submitting = false;
    }
  }

  render() {
    if (!this._open) return nothing;

    return html`
      <div class="cp-convo-form__overlay" @click=${this._onBackdropClick}>
        <div class="cp-convo-form__dialog">
          <div class="cp-convo-form__header">
            <h2 class="cp-convo-form__title">Neue Session</h2>
            <button class="cp-convo-form__close" @click=${this._close}>&times;</button>
          </div>

          <div class="cp-convo-form__body">
            <div class="cp-convo-form__field">
              <label class="cp-convo-form__label">Titel *</label>
              <input
                class="cp-convo-form__input${this._titleError ? ' cp-convo-form__input--error' : ''}"
                type="text"
                placeholder="z.B. Gehaltsverhandlung mit Chef"
                .value=${this._title}
                @input=${(e: Event) => { this._title = (e.target as HTMLInputElement).value; if (this._titleError) this._titleError = ''; }}
                ?disabled=${this._submitting}
                autocomplete="off"
              />
              ${this._titleError ? html`<p class="cp-convo-form__field-error">${this._titleError}</p>` : nothing}
            </div>

            <div class="cp-convo-form__field">
              <label class="cp-convo-form__label">Partner</label>
              <select
                class="cp-convo-form__select"
                @change=${(e: Event) => {
                  const val = (e.target as HTMLSelectElement).value;
                  this._partnerId = val ? Number(val) : undefined;
                }}
                ?disabled=${this._submitting}
              >
                <option value="">Kein Partner</option>
                ${this._people.map(p => html`
                  <option value=${p.id} ?selected=${this._partnerId === p.id}>
                    ${p.full_name}${p.subtitle ? ` — ${p.subtitle}` : ''}
                  </option>
                `)}
              </select>
            </div>

            <div class="cp-convo-form__field">
              <div class="cp-convo-form__label-row">
                <label class="cp-convo-form__label">Beschreibung</label>
                <button
                  class="cp-convo-form__email-btn"
                  @click=${() => { this._emailSearchOpen = !this._emailSearchOpen; }}
                  ?disabled=${this._submitting}
                  type="button"
                >Email anhaengen</button>
              </div>
              <textarea
                class="cp-convo-form__textarea"
                rows="4"
                placeholder="Kontext und Hintergrundinformationen..."
                .value=${this._description}
                @input=${(e: Event) => { this._description = (e.target as HTMLTextAreaElement).value; }}
                ?disabled=${this._submitting}
              ></textarea>
            </div>

            ${this._emailSearchOpen ? this._renderEmailSearch() : nothing}

            <div class="cp-convo-form__row">
              <div class="cp-convo-form__field">
                <label class="cp-convo-form__label">Typ</label>
                <select
                  class="cp-convo-form__select"
                  @change=${(e: Event) => { this._situationType = (e.target as HTMLSelectElement).value as ConvoSituationType; }}
                  ?disabled=${this._submitting}
                >
                  ${SITUATION_TYPES.map(t => html`
                    <option value=${t.value} ?selected=${this._situationType === t.value}>${t.label}</option>
                  `)}
                </select>
              </div>

              <div class="cp-convo-form__field">
                <label class="cp-convo-form__label">Primaerziel</label>
                <input
                  class="cp-convo-form__input"
                  type="text"
                  placeholder="Was willst du erreichen?"
                  .value=${this._primaryGoal}
                  @input=${(e: Event) => { this._primaryGoal = (e.target as HTMLInputElement).value; }}
                  ?disabled=${this._submitting}
                />
              </div>
            </div>

            ${this._error ? html`
              <div class="cp-convo-form__error">${this._error}</div>
            ` : nothing}
          </div>

          <div class="cp-convo-form__footer">
            <button
              class="cp-convo-form__btn cp-convo-form__btn--cancel"
              @click=${this._close}
              ?disabled=${this._submitting}
            >Abbrechen</button>
            <button
              class="cp-convo-form__btn cp-convo-form__btn--submit"
              @click=${this._onSubmit}
              ?disabled=${this._submitting}
            >${this._submitting ? 'Wird erstellt...' : 'Session erstellen'}</button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderEmailSearch() {
    return html`
      <div class="cp-convo-form__email-search">
        <div class="cp-convo-form__email-search-row">
          <input
            class="cp-convo-form__input"
            type="text"
            placeholder="Email-Adresse, Betreff oder Gmail-Suche..."
            .value=${this._emailQuery}
            @input=${(e: Event) => { this._emailQuery = (e.target as HTMLInputElement).value; }}
            @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); void this._searchEmails(); } }}
            ?disabled=${this._emailSearching}
          />
          <button
            class="cp-convo-form__btn cp-convo-form__btn--submit"
            @click=${this._searchEmails}
            ?disabled=${this._emailSearching || !this._emailQuery.trim()}
            type="button"
          >${this._emailSearching ? 'Suche...' : 'Suchen'}</button>
        </div>

        ${this._emailError ? html`<p class="cp-convo-form__field-error">${this._emailError}</p>` : nothing}

        ${this._emailResults.length > 0 ? html`
          <div class="cp-convo-form__email-results">
            ${this._emailResults.map(email => {
              const attached = this._attachedEmailIds.has(email.id);
              return html`
              <div
                class="cp-convo-form__email-result${attached ? ' cp-convo-form__email-result--attached' : ''}"
                @click=${() => { if (!attached) void this._attachEmail(email.id, email.account); }}
                role="button"
                tabindex=${attached ? -1 : 0}
              >
                <div class="cp-convo-form__email-subject">${attached ? '\u2713 ' : ''}${email.subject || '(Kein Betreff)'}</div>
                <div class="cp-convo-form__email-meta">${email.from} — ${email.date}</div>
                <div class="cp-convo-form__email-snippet">${email.snippet}</div>
              </div>
            `;})}
          </div>
        ` : this._emailSearched && !this._emailSearching ? html`
          <p class="cp-convo-form__email-loading">Keine Emails gefunden</p>
        ` : nothing}

        ${this._emailLoading ? html`<p class="cp-convo-form__email-loading">Email wird geladen...</p>` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-convo-session-form': CpConvoSessionForm;
  }
}
